import React, { useState, useRef, useEffect } from 'react';
import { useUIStore, useAuthStore } from '../stores';
import { API_URL } from '../config';
import {
  Upload,
  Image,
  Video,
  FileText,
  Grid,
  List,
  Search,
  Filter,
  Trash2,
  Download,
  Copy,
  MoreHorizontal,
  FolderPlus,
  Plus,
  X,
  Check,
} from 'lucide-react';

export default function ContentLibrary() {
  const [media, setMedia] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef(null);
  const { addToast } = useUIStore();
  const { token } = useAuthStore();

  // Load real files from R2 storage
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/storage/files`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.files) {
          setMedia(data.files.map(f => ({
            id: f.key,
            type: f.key.match(/\.(mp4|mov|webm)$/i) ? 'video' : 'image',
            name: f.key.split('/').pop(),
            url: `${API_URL}/storage/download/${f.key}`,
            size: `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
            createdAt: f.uploaded?.split('T')[0] || '',
          })));
        }
      })
      .catch(() => {});
  }, [token]);
  
  const filteredMedia = media.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || item.type === filterType;
    return matchesSearch && matchesType;
  });
  
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploading(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_URL}/storage/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (data.key) {
          setMedia(prev => [{
            id: data.key,
            type: file.type.startsWith('video') ? 'video' : 'image',
            name: file.name,
            url: `${API_URL}/storage/download/${data.key}`,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
            createdAt: new Date().toISOString().split('T')[0],
          }, ...prev]);
        }
      } catch (err) {
        addToast({ type: 'error', title: 'Upload failed', message: err.message });
      }
    }

    setIsUploading(false);
    
    addToast({
      type: 'success',
      title: 'Upload complete',
      message: `${files.length} file(s) uploaded successfully.`,
    });
  };
  
  const handleDelete = (id) => {
    setMedia(prev => prev.filter(m => m.id !== id));
    setSelectedItems(prev => prev.filter(i => i !== id));
    addToast({ type: 'info', title: 'Deleted', message: 'File removed from library.' });
  };
  
  const handleBulkDelete = () => {
    setMedia(prev => prev.filter(m => !selectedItems.includes(m.id)));
    setSelectedItems([]);
    addToast({ type: 'info', title: 'Deleted', message: `${selectedItems.length} files removed.` });
  };
  
  const toggleSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };
  
  const selectAll = () => {
    if (selectedItems.length === filteredMedia.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredMedia.map(m => m.id));
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Content Library</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your media assets for social posts
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            accept="image/*,video/*"
            multiple
            className="hidden"
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="btn-primary"
          >
            {isUploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload
          </button>
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="card p-4 flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search files..."
            className="input pl-9 py-2"
          />
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input py-2 w-auto"
          >
            <option value="all">All types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
          
          {/* View toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md ${viewMode === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Bulk actions */}
        {selectedItems.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">{selectedItems.length} selected</span>
            <button onClick={handleBulkDelete} className="btn-danger text-sm py-1.5 px-3">
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </button>
            <button onClick={() => setSelectedItems([])} className="btn-secondary text-sm py-1.5 px-3">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      
      {/* Media grid/list */}
      {filteredMedia.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredMedia.map((item) => (
              <div 
                key={item.id}
                className={`group relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                  selectedItems.includes(item.id) 
                    ? 'border-brand-500 ring-2 ring-brand-500/20' 
                    : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                }`}
                onClick={() => toggleSelect(item.id)}
              >
                <div className="aspect-square bg-gray-100 dark:bg-gray-800">
                  <img 
                    src={item.url} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  {item.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Selection indicator */}
                <div className={`absolute top-2 left-2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  selectedItems.includes(item.id) 
                    ? 'bg-brand-500 border-brand-500' 
                    : 'border-white bg-white/50 opacity-0 group-hover:opacity-100'
                }`}>
                  {selectedItems.includes(item.id) && <Check className="w-3 h-3 text-white" />}
                </div>
                
                {/* Info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-xs text-white truncate">{item.name}</p>
                  <p className="text-xs text-gray-300">{item.size}</p>
                </div>
                
                {/* Actions */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    className="p-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card divide-y divide-gray-200 dark:divide-gray-800">
            {filteredMedia.map((item) => (
              <div 
                key={item.id}
                className={`flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${
                  selectedItems.includes(item.id) ? 'bg-brand-50 dark:bg-brand-900/10' : ''
                }`}
                onClick={() => toggleSelect(item.id)}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  selectedItems.includes(item.id) 
                    ? 'bg-brand-500 border-brand-500' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {selectedItems.includes(item.id) && <Check className="w-3 h-3 text-white" />}
                </div>
                
                <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                  <p className="text-sm text-gray-500">{item.type} • {item.size}</p>
                </div>
                
                <p className="text-sm text-gray-400">{item.createdAt}</p>
                
                <div className="flex items-center gap-1">
                  <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
                    <Download className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="card p-12 text-center">
          <FolderPlus className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No media found</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery ? 'Try a different search term' : 'Upload your first file to get started'}
          </p>
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Files
          </button>
        </div>
      )}
      
      {/* Storage info */}
      <div className="card p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Storage used</p>
          <p className="text-xs text-gray-500">28.3 MB of 1 GB</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="w-[3%] h-full bg-brand-500 rounded-full" />
          </div>
          <span className="text-sm text-gray-500">3%</span>
        </div>
      </div>
    </div>
  );
}
